import React, { useState, useEffect, useCallback, forwardRef } from 'react';
import { Table, Card, Tag, Space, Button, Tooltip, message, Modal, Input, Select, Row, Col, Statistic } from 'antd';
import { EditOutlined, DeleteOutlined, DollarOutlined, SearchOutlined } from '@ant-design/icons';
import { getAllCredits, deleteCredit, getAllPartners } from '../api/accounting';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';

const Credits = forwardRef(({ filters, selectedBrand, onAffectCredit, onCreateCredit }, ref) => {
  const [loading, setLoading] = useState(false);
  const [credits, setCredits] = useState([]);
  const [partners, setPartners] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [loadingPartners, setLoadingPartners] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(500);
  const [totalItems, setTotalItems] = useState(0);
  const [summary, setSummary] = useState({
    count: 0,
    total_amount: 0,
    allocated_amount: 0
  });
  const navigate = useNavigate();

  // Fetch partners for dropdown
  const fetchPartners = useCallback(async () => {
    setLoadingPartners(true);
    try {
      const response = await getAllPartners();
      
      // Handle different response formats
      if (Array.isArray(response)) {
        // If response is already an array
        setPartners(response);
      } else if (response && Array.isArray(response.data)) {
        // If response is an object with data array (Axios response format)
        setPartners(response.data);
      } else if (response && response.results && Array.isArray(response.results)) {
        // If response is a paginated object with results array
        setPartners(response.results);
      } else {
        console.error('Invalid partners response format:', response);
        setPartners([]);
      }
    } catch (error) {
      console.error('Error fetching partners:', error);
      message.error('Failed to load partners');
      setPartners([]);
    } finally {
      setLoadingPartners(false);
    }
  }, []);

  useEffect(() => {
    fetchPartners();
  }, [fetchPartners]);

  const fetchCredits = useCallback(async () => {
    setLoading(true);
    try {
      // Create API-compatible filter object
      const apiFilters = {};
      
      // Handle date range
      if (filters.dateRange && filters.dateRange.length === 2) {
        // Format dates for API if they exist
        if (filters.dateRange[0]) {
          apiFilters.start_date = filters.dateRange[0].format('YYYY-MM-DD');
        }
        if (filters.dateRange[1]) {
          apiFilters.end_date = filters.dateRange[1].format('YYYY-MM-DD');
        }
      }
      
      // Handle partner type filter
      if (filters.partnerType && filters.partnerType !== 'all') {
        apiFilters.partner_type = filters.partnerType;
      }
      
      // Handle credit type filter based on partner type
      if (filters.partnerType === 'customer') {
        apiFilters.credit_type = 'invoice';
      } else if (filters.partnerType === 'vendor') {
        apiFilters.credit_type = 'bill';
      }
      
      // Handle brand filter
      if (selectedBrand) {
        apiFilters.brand = selectedBrand.id;
      }
      
      // Add partner filter if selected
      if (selectedPartner) {
        apiFilters.partner_id = selectedPartner;
      }
      
      // Add search query if provided
      if (searchQuery && searchQuery.trim() !== '') {
        apiFilters.search = searchQuery.trim();
      }
      
      const response = await getAllCredits(apiFilters);
      
      // Handle Axios response format
      const responseData = response.data ? response : { data: response };
      
      // Check if response is paginated (has results property)
      let creditData = [];
      
      // First check if the response or response.data has results property
      if (responseData.data && responseData.data.results) {
        // Handle paginated response in responseData.data
        creditData = responseData.data.results;
        
        // Update pagination state
        setTotalItems(responseData.data.total || 0);
        
        // Update summary statistics from backend
        if (responseData.data.summary) {
          setSummary(responseData.data.summary);
        }
      } else if (response && response.results) {
        // Handle paginated response directly in response
        creditData = response.results;
        
        // Update pagination state
        setTotalItems(response.total || 0);
        
        // Update summary statistics from backend
        if (response.summary) {
          setSummary(response.summary);
        }
      } else if (Array.isArray(responseData.data)) {
        // Handle array response in responseData.data
        creditData = responseData.data;
      } else if (Array.isArray(response)) {
        // Handle array response directly
        creditData = response;
      }
      
      if (!creditData || !Array.isArray(creditData)) {
        message.error('Received invalid data format from server');
        setCredits([]);
        return;
      }
      
      // Transform the data to match our component's expected format
      const formattedCredits = creditData.map(credit => {
        // Calculate allocation percentage
        const total = parseFloat(credit.total_amount || 0);
        const allocatedAmount = parseFloat(credit.allocated_amount || 0);
        const percentAllocated = total > 0 ? (allocatedAmount / total) * 100 : 0;
        
        // Determine allocation status
        let allocationStatus;
        if (percentAllocated >= 99.99) {
          allocationStatus = { type: 'fully_allocated', label: 'Fully Allocated', color: 'success' };
        } else if (percentAllocated > 0) {
          allocationStatus = { type: 'partially_allocated', label: 'Partially Allocated', color: 'warning' };
        } else {
          allocationStatus = { type: 'unallocated', label: 'Unallocated', color: 'default' };
        }
        
        return {
          id: credit.id,
          number: credit.number || `CR-${credit.id}`,
          partner: credit.partner?.name || 'Unknown',
          partnerType: credit.credit_type === 'invoice' ? 'customer' : 'vendor',
          credit_type: credit.credit_type || 'invoice',
          issueDate: credit.issue_date,
          total: parseFloat(credit.total_amount || 0),
          allocated_amount: parseFloat(credit.allocated_amount || 0),
          remaining_amount: parseFloat(credit.total_amount || 0) - parseFloat(credit.allocated_amount || 0),
          // Keep the original data for reference
          originalData: credit,
          // Use credit_allocations from the API format
          allocations: credit.credit_allocations?.map(allocation => ({
            id: allocation.id,
            amount: allocation.amount,
            allocation_date: allocation.allocation_date,
            invoice_number: allocation.invoice_number || '',
          })) || [],
          allocationStatus: allocationStatus,
          percentAllocated: percentAllocated,
          // Store the currency for display
          currency: credit.currency || '$',
          // Add tax_rate, subtotal, and discount_amount fields
          tax_rate: credit.tax_rate,
          subtotal: credit.subtotal,
          discount_amount: credit.discount_amount
        };
      });
      
      setCredits(formattedCredits);
    } catch (error) {
      console.error('Error fetching credits:', error);
      message.error(`Failed to fetch credits: ${error.message || 'Unknown error'}`);
      setCredits([]);
    } finally {
      setLoading(false);
    }
  }, [filters, selectedBrand, searchQuery, selectedPartner]);

  // Fetch credits when filters change
  useEffect(() => {
    fetchCredits();
  }, [fetchCredits, filters, selectedBrand]);

  // Handle edit credit
  const handleEditCredit = (creditId) => {
    if (onCreateCredit) {
      onCreateCredit(creditId);
    } else {
      navigate(`/accounting/payment-allocation/create-credit?id=${creditId}`);
    }
  };

  // Handle delete credit
  const handleDeleteCredit = async (creditId) => {
    Modal.confirm({
      title: 'Confirm Deletion',
      content: 'Are you sure you want to delete this credit? This action cannot be undone.',
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'No, Cancel',
      onOk: async () => {
        try {
          await deleteCredit(creditId);
          message.success('Credit deleted successfully');
          fetchCredits(); // Refresh the list
        } catch (error) {
          console.error('Error deleting credit:', error);
          message.error('Failed to delete credit');
        }
      },
    });
  };

  // Handle search
  const handleSearch = (value) => {
    setSearchQuery(value);
    setCurrentPage(1); // Reset to first page when searching
  };

  // Handle partner selection
  const handlePartnerChange = (value) => {
    setSelectedPartner(value);
    setCurrentPage(1); // Reset to first page when changing partner
  };

  // Handle pagination
  const handleTableChange = (pagination) => {
    setCurrentPage(pagination.current);
  };

  // Define table columns
  const columns = [
    {
      title: 'Credit #',
      dataIndex: 'number',
      key: 'number',
      sorter: (a, b) => a.number.localeCompare(b.number),
      render: (text, record) => (
        <a onClick={() => handleEditCredit(record.id)}>{text}</a>
      ),
    },
    {
      title: 'Date',
      dataIndex: 'issueDate',
      key: 'issueDate',
      sorter: (a, b) => moment(a.issueDate).unix() - moment(b.issueDate).unix(),
      render: (date) => moment(date).format('YYYY-MM-DD'),
    },
    {
      title: 'Type',
      dataIndex: 'credit_type',
      key: 'credit_type',
      render: (type) => (
        <Tag color={type === 'invoice' ? 'blue' : 'green'}>
          {type === 'invoice' ? 'Invoice' : 'Bill'}
        </Tag>
      ),
      sorter: (a, b) => a.credit_type.localeCompare(b.credit_type),
    },
    {
      title: 'From',
      key: 'from',
      render: (_, record) => {
        return record.credit_type === 'bill' 
          ? record.partner 
          : record.originalData.brand?.brand_name || 'Unknown';
      },
      sorter: (a, b) => {
        const aFrom = a.credit_type === 'bill' ? a.partner : (a.originalData.brand?.brand_name || '');
        const bFrom = b.credit_type === 'bill' ? b.partner : (b.originalData.brand?.brand_name || '');
        return aFrom.localeCompare(bFrom);
      },
    },
    {
      title: 'To',
      key: 'to',
      render: (_, record) => {
        return record.credit_type === 'invoice' 
          ? record.partner 
          : record.originalData.brand?.brand_name || 'Unknown';
      },
      sorter: (a, b) => {
        const aTo = a.credit_type === 'invoice' ? a.partner : (a.originalData.brand?.brand_name || '');
        const bTo = b.credit_type === 'invoice' ? b.partner : (b.originalData.brand?.brand_name || '');
        return aTo.localeCompare(bTo);
      },
    },
    {
      title: 'Total',
      dataIndex: 'total',
      key: 'total',
      sorter: (a, b) => a.total - b.total,
      render: (total, record) => `${record.currency} ${total.toFixed(2)}`,
    },
    {
      title: 'Allocated',
      dataIndex: 'allocated_amount',
      key: 'allocated_amount',
      sorter: (a, b) => a.allocated_amount - b.allocated_amount,
      render: (allocated, record) => `${record.currency} ${allocated.toFixed(2)}`,
    },
    {
      title: 'Remaining',
      dataIndex: 'remaining_amount',
      key: 'remaining_amount',
      sorter: (a, b) => a.remaining_amount - b.remaining_amount,
      render: (remaining, record) => `${record.currency} ${remaining.toFixed(2)}`,
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, record) => (
        <Tag color={record.allocationStatus.color}>
          {record.allocationStatus.label}
        </Tag>
      ),
      sorter: (a, b) => a.allocationStatus.type.localeCompare(b.allocationStatus.type),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Edit">
            <Button 
              type="primary" 
              icon={<EditOutlined />} 
              size="small" 
              onClick={() => handleEditCredit(record.id)}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button 
              type="danger" 
              icon={<DeleteOutlined />} 
              size="small" 
              onClick={() => handleDeleteCredit(record.id)}
            />
          </Tooltip>
          <Tooltip title="Allocate Credit">
            <Button 
              type="default" 
              icon={<DollarOutlined />} 
              size="small" 
              onClick={() => onAffectCredit ? onAffectCredit(record.id) : navigate(`/accounting/payment-allocation/credit-allocation?creditId=${record.id}`)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];


  return (
    <div className="credits-page">
      <Card title="Credits List" extra={
        <Button 
          type="primary" 
          onClick={() => onCreateCredit ? onCreateCredit() : navigate('/accounting/payment-allocation/create-credit')}
          disabled={!selectedBrand}
        >
          Create Credit
        </Button>
      }>
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={8}>
            <Input.Search
              placeholder="Search credits..."
              allowClear
              enterButton={<SearchOutlined />}
              onSearch={handleSearch}
              style={{ width: '100%' }}
            />
          </Col>
 
        </Row>

        <div className="summary-stats" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col span={8}>
              <Card size="small">
                <Statistic
                  title="Total Credits"
                  value={summary.count}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card size="small">
                <Statistic
                  title="Total Amount"
                  value={summary.total_amount}
                  precision={2}
                  prefix="$"
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card size="small">
                <Statistic
                  title="Allocated Amount"
                  value={summary.allocated_amount}
                  precision={2}
                  prefix="$"
                />
              </Card>
            </Col>
          </Row>
        </div>

        <Table
          columns={columns}
          dataSource={credits}
          rowKey="id"
          loading={loading}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: totalItems,
            showSizeChanger: false,
          }}
          onChange={handleTableChange}
          scroll={{ x: 1200 }}
        />
      </Card>
    </div>
  );
});
Credits.displayName = 'Credits';

export default Credits;
